import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../../lib/api';
import { 
  FileText, 
  UploadCloud, 
  CheckCircle2, 
  AlertCircle, 
  Send, 
  BookOpen, 
  Clock, 
  HelpCircle,
  X,
  FileCheck
} from 'lucide-react';
import { 
  PROJECT_STAGES_LIST, 
  PUBLISHING_GENRES, 
  PUBLISHING_REQUIREMENTS_LIST 
} from '@ftt/shared';

const enquiryFormSchema = z.object({
  fullName: z.string().min(1, 'Please enter your full name'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  organization: z.string().optional(),
  country: z.string().min(1, 'Please enter your country'),
  projectType: z.string().min(1, 'Please select or enter project type'),
  bookTitle: z.string().min(1, 'Please enter the working book title'),
  authorName: z.string().min(1, 'Please enter author name(s)'),
  genre: z.string().min(1, 'Please select a genre/category'),
  wordCount: z.string().optional(),
  stage: z.enum([
    'IDEA_CONCEPT',
    'OUTLINE_SYNOPSIS',
    'FIRST_DRAFT',
    'REVISED_DRAFT',
    'COMPLETED_MANUSCRIPT',
    'PREVIOUSLY_PUBLISHED',
  ]),
  requirements: z.array(z.string()).min(1, 'Please select at least one publishing requirement'),
  expectedTimeline: z.string().optional(),
  additionalInfo: z.string().optional(),
  consent: z.boolean().refine((val) => val === true, 'You must acknowledge the publishing & privacy terms'),
});

type EnquiryFormData = z.infer<typeof enquiryFormSchema>;

export const EnquiryPage: React.FC = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState<{ referenceNo: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EnquiryFormData>({
    resolver: zodResolver(enquiryFormSchema),
    defaultValues: {
      stage: 'COMPLETED_MANUSCRIPT',
      requirements: ['Manuscript Assessment', 'Developmental / Structural Editing'],
      projectType: 'Book / Monograph',
      consent: false,
    },
  });

  const selectedRequirements = watch('requirements') || [];

  const handleRequirementToggle = (reqName: string) => {
    if (selectedRequirements.includes(reqName)) {
      setValue('requirements', selectedRequirements.filter((r) => r !== reqName), { shouldValidate: true });
    } else {
      setValue('requirements', [...selectedRequirements, reqName], { shouldValidate: true });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: EnquiryFormData) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('fullName', data.fullName);
      formData.append('email', data.email);
      if (data.phone) formData.append('phone', data.phone);
      if (data.organization) formData.append('organization', data.organization);
      formData.append('country', data.country);
      formData.append('projectType', data.projectType);
      formData.append('bookTitle', data.bookTitle);
      formData.append('authorName', data.authorName);
      formData.append('genre', data.genre);
      if (data.wordCount) formData.append('wordCount', data.wordCount);
      formData.append('stage', data.stage);
      formData.append('requirements', JSON.stringify(data.requirements));
      if (data.expectedTimeline) formData.append('expectedTimeline', data.expectedTimeline);
      if (data.additionalInfo) formData.append('additionalInfo', data.additionalInfo);

      files.forEach((file) => {
        formData.append('files', file);
      });

      const res = await api.post('/enquiries/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data?.success) {
        setSubmissionSuccess({ referenceNo: res.data.data.referenceNo });
      }
    } catch (err: any) {
      setErrorMessage(err.response?.data?.error?.message || 'Failed to submit publishing enquiry. Please review the inputs and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-12 py-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
          Acquisitions &amp; Proposal Portal
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark">
          Publishing Enquiry &amp; RFQ
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
          Submit your book proposal or completed manuscript for formal assessment by our editorial committee. Every proposal receives a dedicated reference number and confidential review.
        </p>
      </section>

      {/* Submission Success View */}
      {submissionSuccess ? (
        <div className="p-8 sm:p-14 rounded-2xl bg-white border border-editorial-border shadow-md text-center space-y-6">
          <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8" />
          </div>

          <div className="space-y-2">
            <h2 className="font-serif text-3xl font-bold text-slate-900">
              Publishing Enquiry Successfully Logged
            </h2>
            <p className="text-sm text-slate-600 max-w-lg mx-auto leading-relaxed">
              Your manuscript submission and project specifications have been securely transmitted to the For The Truth Publishing acquisitions board.
            </p>
          </div>

          <div className="p-6 rounded-xl bg-editorial-parchment border border-brand-200 inline-block max-w-md w-full">
            <span className="text-xs text-slate-500 font-mono block mb-1 uppercase tracking-wider">
              Enquiry Reference Number
            </span>
            <span className="text-2xl font-mono font-bold text-brand-800 tracking-wider">
              {submissionSuccess.referenceNo}
            </span>
            <p className="text-[11px] text-slate-500 mt-2">
              An acknowledgement email with review milestones has been sent to your inbox.
            </p>
          </div>

          <div className="pt-4 flex flex-wrap items-center justify-center gap-4 text-xs font-medium">
            <a
              href="/"
              className="px-6 py-3 rounded-lg bg-editorial-dark text-white font-bold hover:bg-slate-800"
            >
              Return to Homepage
            </a>
            <a
              href="/login"
              className="px-6 py-3 rounded-lg bg-brand-50 text-brand-800 border border-brand-200 font-bold hover:bg-brand-100"
            >
              Client Login Area &rarr;
            </a>
          </div>
        </div>
      ) : (
        /* Comprehensive Multi-Section Form */
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
          
          {errorMessage && (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-3">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* 1. Author & Contact Information */}
          <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
            <div className="border-b border-slate-100 pb-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-600 font-bold">Section 1</span>
              <h2 className="font-serif text-xl font-bold text-slate-900">Author &amp; Organization Details</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Primary Contact Full Name *
                </label>
                <input
                  type="text"
                  {...register('fullName')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. Dr. Arthur Pendleton"
                />
                {errors.fullName && <p className="text-rose-600 text-[11px] mt-1">{errors.fullName.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="author@example.com"
                />
                {errors.email && <p className="text-rose-600 text-[11px] mt-1">{errors.email.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  {...register('phone')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Organization / Ministry / Seminary
                </label>
                <input
                  type="text"
                  {...register('organization')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. Grace Theological Seminary"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Country *
                </label>
                <input
                  type="text"
                  {...register('country')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. United Kingdom / United States"
                />
                {errors.country && <p className="text-rose-600 text-[11px] mt-1">{errors.country.message}</p>}
              </div>
            </div>
          </div>

          {/* 2. Publication / Manuscript Overview */}
          <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
            <div className="border-b border-slate-100 pb-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-600 font-bold">Section 2</span>
              <h2 className="font-serif text-xl font-bold text-slate-900">Project &amp; Manuscript Overview</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Book / Publication Title *
                </label>
                <input
                  type="text"
                  {...register('bookTitle')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. The Solas of the Reformation"
                />
                {errors.bookTitle && <p className="text-rose-600 text-[11px] mt-1">{errors.bookTitle.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Author Name(s) as to appear on Cover *
                </label>
                <input
                  type="text"
                  {...register('authorName')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. Arthur Pendleton, Ph.D."
                />
                {errors.authorName && <p className="text-rose-600 text-[11px] mt-1">{errors.authorName.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Primary Genre / Category *
                </label>
                <select
                  {...register('genre')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden bg-white"
                >
                  <option value="">Select a Genre</option>
                  {PUBLISHING_GENRES.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
                {errors.genre && <p className="text-rose-600 text-[11px] mt-1">{errors.genre.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Current Project Stage *
                </label>
                <select
                  {...register('stage')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden bg-white"
                >
                  {PROJECT_STAGES_LIST.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Approximate Word Count
                </label>
                <input
                  type="number"
                  {...register('wordCount')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. 75000"
                />
              </div>
            </div>
          </div>

          {/* 3. Publishing Requirements */}
          <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
            <div className="border-b border-slate-100 pb-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-600 font-bold">Section 3</span>
              <h2 className="font-serif text-xl font-bold text-slate-900">Publishing Scope &amp; Services Required</h2>
              <p className="text-xs text-slate-500 mt-1">Select all services you would like included in your publishing proposal.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PUBLISHING_REQUIREMENTS_LIST.map((reqName) => {
                const checked = selectedRequirements.includes(reqName);
                return (
                  <div
                    key={reqName}
                    onClick={() => handleRequirementToggle(reqName)}
                    className={`p-3.5 rounded-lg border cursor-pointer flex items-center gap-3 text-xs transition-all select-none ${
                      checked
                        ? 'bg-brand-50 border-brand-500 text-brand-950 font-semibold'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded flex items-center justify-center border ${
                      checked ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-300 bg-white'
                    }`}>
                      {checked && <CheckCircle2 className="w-3.5 h-3.5" />}
                    </div>
                    <span>{reqName}</span>
                  </div>
                );
              })}
            </div>
            {errors.requirements && <p className="text-rose-600 text-[11px]">{errors.requirements.message}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Expected Publication Target Timeline
                </label>
                <input
                  type="text"
                  {...register('expectedTimeline')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="e.g. 6 months / Autumn 2026"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Additional Project Notes / Theological Summary
                </label>
                <input
                  type="text"
                  {...register('additionalInfo')}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
                  placeholder="Brief synopsis, primary themes, or specific goals"
                />
              </div>
            </div>
          </div>

          {/* 4. Secure File Attachments */}
          <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
            <div className="border-b border-slate-100 pb-3">
              <span className="text-[10px] font-mono uppercase tracking-widest text-brand-600 font-bold">Section 4</span>
              <h2 className="font-serif text-xl font-bold text-slate-900">Manuscript &amp; Proposal Files</h2>
              <p className="text-xs text-slate-500 mt-1">Upload manuscript drafts, synopsis, sample chapters, or proposal outlines (PDF, DOCX, EPUB, RTF, ZIP - Max 50MB per file).</p>
            </div>

            <div className="border-2 border-dashed border-slate-300 hover:border-brand-500 rounded-xl p-8 text-center space-y-3 bg-slate-50/50 transition-colors">
              <UploadCloud className="w-10 h-10 text-brand-600 mx-auto" />
              <div className="text-xs text-slate-600">
                <label className="font-bold text-brand-700 hover:underline cursor-pointer">
                  <span>Browse and select files</span>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.docx,.doc,.epub,.txt,.rtf,.zip"
                  />
                </label>
                <span className="block text-[11px] text-slate-400 mt-1">or drag &amp; drop files into this zone</span>
              </div>
            </div>

            {files.length > 0 && (
              <div className="space-y-2">
                <span className="text-xs font-bold text-slate-900 uppercase tracking-wider block">
                  Attached Files ({files.length}):
                </span>
                <div className="space-y-1.5">
                  {files.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs"
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        <FileText className="w-4 h-4 text-brand-600 shrink-0" />
                        <span className="font-medium text-slate-800 truncate">{file.name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">({Math.round(file.size / 1024)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="text-slate-400 hover:text-rose-600 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 5. Terms & Submission */}
          <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="consent"
                {...register('consent')}
                className="w-4 h-4 mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <label htmlFor="consent" className="text-xs text-slate-600 leading-relaxed select-none">
                I acknowledge that this submission will be confidentially evaluated by the For The Truth Publishing editorial acquisitions committee. I understand that submitting a proposal does not bind either party to publish until a formal publishing agreement is executed.
              </label>
            </div>
            {errors.consent && <p className="text-rose-600 text-[11px]">{errors.consent.message}</p>}

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-md transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <span>Generating Reference &amp; Submitting...</span>
                ) : (
                  <>
                    <span>Submit Publishing Proposal (RFQ)</span>
                    <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>

        </form>
      )}

    </div>
  );
};
